/**
 * SentimentInfluenceLayer — invisible bias layer derived from Fear & Greed.
 * 
 * This is NOT a visible UI element. It produces bias values that subtly
 * modulate existing moods, expressions, aura, and copy tone.
 * 
 * Design rule: 10–25% flavor, never the main driver.
 * Mempool + blocks = what happens. Sentiment = how it feels psychologically.
 */

import { SentimentBucket, SentimentData, DEFAULT_SENTIMENT } from '@/lib/types/sentiment';

// ── Bias output ──

export interface SentimentInfluence {
  /** -1 (fearful) to +1 (overconfident) */
  confidenceBias: number;
  /** -1 to +1: positive = more cautious */
  cautionBias: number;
  /** -1 (cold) to +1 (warm/polished) */
  warmthBias: number;
  /** -1 (raw/rough) to +1 (glossy/polished) */
  polishBias: number;
  /** -1 to +1: positive = smug/cocky undertone */
  smugBias: number;
  /** -1 to +1: positive = guarded/defensive */
  guardedBias: number;
}

// ── Config: easy to tune globally ──

export const SENTIMENT_INFLUENCE_CONFIG = {
  /** Master strength multiplier (0 = off, 1 = full). Keep 0.15–0.25 */
  globalStrength: 0.20,
  /** Per-bucket bias definitions (raw, before globalStrength) */
  buckets: {
    extreme_fear: {
      confidenceBias: -0.8,
      cautionBias: 0.9,
      warmthBias: -0.4,
      polishBias: -0.6,
      smugBias: -0.9,
      guardedBias: 0.9,
    },
    fear: {
      confidenceBias: -0.4,
      cautionBias: 0.5,
      warmthBias: -0.2,
      polishBias: -0.3,
      smugBias: -0.5,
      guardedBias: 0.5,
    },
    neutral: {
      confidenceBias: 0,
      cautionBias: 0,
      warmthBias: 0,
      polishBias: 0,
      smugBias: 0,
      guardedBias: 0,
    },
    greed: {
      confidenceBias: 0.5,
      cautionBias: -0.3,
      warmthBias: 0.4,
      polishBias: 0.5,
      smugBias: 0.3,
      guardedBias: -0.4,
    },
    extreme_greed: {
      confidenceBias: 0.85,
      cautionBias: -0.5,
      warmthBias: 0.5,
      polishBias: 0.8,
      smugBias: 0.7,
      guardedBias: -0.6,
    },
  } satisfies Record<SentimentBucket, SentimentInfluence>,
};

// ── Derive influence from sentiment data ──

const NEUTRAL_INFLUENCE: SentimentInfluence = {
  confidenceBias: 0, cautionBias: 0, warmthBias: 0,
  polishBias: 0, smugBias: 0, guardedBias: 0,
};

export function deriveSentimentInfluence(sentiment: SentimentData = DEFAULT_SENTIMENT): SentimentInfluence {
  if (sentiment.timestamp === 0) return NEUTRAL_INFLUENCE;

  const raw = SENTIMENT_INFLUENCE_CONFIG.buckets[sentiment.bucket];
  const s = SENTIMENT_INFLUENCE_CONFIG.globalStrength;

  return {
    confidenceBias: raw.confidenceBias * s,
    cautionBias: raw.cautionBias * s,
    warmthBias: raw.warmthBias * s,
    polishBias: raw.polishBias * s,
    smugBias: raw.smugBias * s,
    guardedBias: raw.guardedBias * s,
  };
}

// ── Apply functions ──

/** Apply sentiment influence to baseline visual parameters (face, aura, posture) */
export function applySentimentToBaseline(
  baseline: {
    smileBias: number;
    eyeTension: number;
    postureBias: number;
    auraBrightness: number;
    colorWarmth: number;
    hoverHeight: number;
    blinkInterval: number;
    faceOpenness: number;
  },
  influence: SentimentInfluence,
): typeof baseline {
  const result = { ...baseline };

  // Smile: confidence pushes smile up, caution/guarded pushes it down
  result.smileBias = clamp(result.smileBias + influence.confidenceBias * 0.25 + influence.smugBias * 0.1, -1, 1);

  // Eye tension: caution/guarded = more tense, confidence = more relaxed
  result.eyeTension = clamp(result.eyeTension + influence.cautionBias * 0.15 + influence.guardedBias * 0.1 - influence.confidenceBias * 0.08, 0, 1);

  // Posture: confidence = upright, caution = drooped
  result.postureBias = clamp(result.postureBias + influence.confidenceBias * 0.2 - influence.cautionBias * 0.1, -1, 1);

  // Aura brightness: polish/warmth = brighter, guarded = dimmer
  result.auraBrightness = clamp(result.auraBrightness + influence.polishBias * 0.12 + influence.warmthBias * 0.08, 0, 1);

  // Color warmth: warmth bias directly
  result.colorWarmth = clamp(result.colorWarmth + influence.warmthBias * 0.15, 0, 1);

  // Hover height: confidence lifts, caution sinks
  result.hoverHeight = result.hoverHeight + influence.confidenceBias * 2;

  // Blink interval: guarded = faster blinks (nervous), confident = slower
  result.blinkInterval = result.blinkInterval * (1 - influence.guardedBias * 0.12 + influence.confidenceBias * 0.08);

  // Face openness: confidence = more open, guarded = more closed
  result.faceOpenness = clamp(result.faceOpenness + influence.confidenceBias * 0.08 - influence.guardedBias * 0.06, 0, 1);

  return result;
}

/** Apply sentiment influence to environment atmosphere */
export function applySentimentToEnvironment(
  env: {
    ambientWarmth: number;
    ambientBrightness: number;
    glowIntensity: number;
    tensionLevel: number;
    stillness: number;
  },
  influence: SentimentInfluence,
): typeof env {
  const result = { ...env };

  // Warmth: warm bias directly
  result.ambientWarmth = clamp(result.ambientWarmth + influence.warmthBias * 0.12, 0, 1);

  // Brightness: polish = glossier, guarded = dimmer
  result.ambientBrightness = clamp(result.ambientBrightness + influence.polishBias * 0.1, 0, 1);

  // Glow: confidence/polish = stronger, guarded = muted
  result.glowIntensity = clamp(result.glowIntensity + influence.polishBias * 0.08 - influence.guardedBias * 0.05, 0, 1);

  // Tension: caution adds subtle tension
  result.tensionLevel = clamp(result.tensionLevel + influence.cautionBias * 0.08, 0, 1);

  // Stillness: guarded = more still (frozen), confident = slightly less still
  result.stillness = clamp(result.stillness + influence.guardedBias * 0.05, 0, 1);

  return result;
}

/** Get verdict tone suffix based on sentiment (used by story engine) */
export function getSentimentVerdictTone(influence: SentimentInfluence): 'fearful' | 'cautious' | 'neutral' | 'confident' | 'euphoric' {
  const net = influence.confidenceBias - influence.cautionBias;
  if (net < -0.12) return 'fearful';
  if (net < -0.04) return 'cautious';
  if (net > 0.12) return 'euphoric';
  if (net > 0.04) return 'confident';
  return 'neutral';
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
